<template>
   <div class='rankings'>
      <process-header :step="8"/>
      <div class="body">
         <div class='step-title'>
            <span>Activity Rankings</span>
         </div>
         <div style="overflow: auto;">
            <el-table :data="activities" :default-sort = "{prop: 'priority', order: 'descending'}">
               <el-table-column label="Selected" fixed width="80">
                  <template slot-scope="scope">
                     <input
                        type="checkbox"
                        :ref="`checkboxFor${scope.row.id}`"
                        v-model="scope.row.is_selected"
                        @click="event => handleActivitySelection(event, scope.row)"
                        title="Select activity"
                     />
                  </template>
               </el-table-column>
               <el-table-column label="Code">
                  <template slot-scope="scope" width="300">
                     <span :content="`Goal: ${scope.row.goal}<br>Objective: ${scope.row.objective}`" v-tippy>
                        {{ scope.row.code }}
                     </span>
                  </template>
               </el-table-column>
               <el-table-column
                  v-for="{label, prop, width, sortable, formatter} in tableColumns"
                  :key="prop"
                  :prop="prop"
                  :width="width"
                  :sortable="sortable"
                  :label="label"
                  :formatter="formatter"
               />
            </el-table>
         </div>
         <save-continue-btns
            :backRoute="{name: 'fundings'}"
            @save="save"
            @save-and-continue="saveAndContinue"
            not-for-form
         />
      </div>
   </div>
</template>

<script>
import { Table, TableColumn } from 'element-ui';
import ProcessHeader from "../Header";
import SaveContinueBtns from '@/components/molecules/Home/Plan/SaveContinueBtns';
import { fetchWithAuthToken, postWithAuthToken } from '../../../../../axios';
import {
  formatNumberNepaliStyle,
   hideLoading,
   showError,
   showLoading,
   showSuccess,
   confirm,
} from '../../../../../js/ui-helpers';

const formatter = (row,column) => {
   return formatNumberNepaliStyle(row[column.property]);
};
const tableColumns = [
   { label: "Activity",          prop: "description",         width: 260, sortable: true },
   { label: "Priority",          prop: "priority_assessment", width: 150, sortable: true },
   { label: "Est. Budget (NPR)", prop: "estimated_budget",    width: 170, sortable: true, formatter },
   { label: "Funding (NPR)",     prop: "funding",             width: 150, sortable: true, formatter },
   { label: "Ranking",           prop: "ranking",             width: 100, sortable: true },
];

export default {
   name: "rankings",
   components: {
      ProcessHeader,
      SaveContinueBtns,
      'el-table': Table,
      'el-table-column': TableColumn,
   },
   data()
   {
      return {
         showForm: false,
         tableColumns,
         activities: [],
      };
   },
   computed: {
      planId() {
         return this.$route.params.id;
      },
   },
   async created()
   {
      try
      {
         showLoading('loading activities');
         const {data: activities} = await fetchWithAuthToken(`api/plan/${this.planId}/rankings/`);
         this.activities = this.getFormattedActivities(activities);
      }
      catch (error)
      {
         console.log(error);
      }
      hideLoading();
   },
   methods: {
      getFormattedActivities(activities)
      {
         const maxPriority = Math.max( ...activities.map(activity => activity.priority_assessment) );
         return activities.map(activity =>
         {
            // normalizing the score by dividing it by maxPriority,
            // so that score can only be between 0 & 1 
            // and so is the case for fundingScore (ranges from 0 to 1)
            const priorityScore = activity.priority_assessment/maxPriority;
            const fundingScore = (activity.funding >= activity.estimated_budget) ? 1 : 0;
            const ranking = Number((priorityScore + fundingScore).toFixed(2));
            return { ...activity, ranking };
         });
      },
      handleActivitySelection(event, activity)
      {
         if (event.target.checked && activity.funding < activity.estimated_budget)
            confirm({
               title: 'Activity Selection',
               msg: `Are you sure you want to select this activity? The funding source for this activity is 
               less than the estimated budget by NPR ${formatNumberNepaliStyle(activity.estimated_budget - activity.funding)}`,
               onFailure: () => {
                  activity.is_selected = false;
               },
            });

      },
      getPostActivities()
      {
         const postFields = ["id", "is_selected"];
         return this.activities.map(activity =>
         { 
            return postFields.reduce((acc, field) => ({...acc, [field]: activity[field] }), {});
         });
      },
      save({goToNext=false}={})
      {
         postWithAuthToken(`api/plan/${this.planId}/rankings/`, this.getPostActivities())
         .then(() => showSuccess('Activity Selection', 'Successfully save the selected activities'))
         .catch(() => showError('Activity Selection', "Failed to save"));
         if (goToNext) 
         {
            this.$router.push({name: 'sa'});
         }
      },
      saveAndContinue()
      {
         this.save({goToNext: true});
      }
  },
};
</script>

<style lang="scss" scoped>
.rankings {
   font-family: $primary-font;
   color: $font-color;
   > .body {
      padding: 0.6em;
      .goal {
         background: $secondary-bg;
         padding: 0.5em;
         margin-top: 1em;
         border-radius: 5px;
         .goal-title {
            padding: 0.3em 0.2em;
            font-weight: 600;
            margin-bottom: 0.3em;
         }
         .objective {
            padding: 0.2em;
            &:not(:last-child) {
               margin-bottom: .7em;
            }
            border-radius: 5px;
            background: white;
            .objective-head {
               .objective-title {
                  font-weight: 600;
                  padding: 0.5em 0.2em;
               }
               display: flex;
               justify-content: space-between;
            }
         }
      }
   }
   .warning {
      padding: 4px 2px;
      background: $warning-color;
      border-radius: 2px;
      // color: white;
      font-size: 10px;
   }
   .save-btn {
      background: $primary-color;
      color: white;
      border-radius: 16px;
      padding: 8px 12px;
      margin-left: 1em;
      margin-bottom: 0.7em;
   }

   .step-title {
      span {
         font-family: $secondary-font;
         font-weight: 500;
         font-size: 1.05em;
      }
      font-family: inherit;
      display: flex;
      justify-content: space-between;
   }
   .btn-group {
      display: flex;
      > * {
         margin-right: 0.3em;
         a {
            color:inherit;
         }
         &:last-child {
            margin-right: 0;
         }
      }
   }
}
</style>
